import { useEffect, useRef } from "react";

import { useSessionStorage } from "./useSessionStorage";

const VISIBILITY_CHECK_KEY = "alerts_visibility_checked";
const VISIBILITY_THRESHOLD = 0.5;

type UseContainerVisibilityOptions = {
    onNotVisible?: () => void;
    threshold?: number;
    enabled?: boolean;
};

export const useContainerVisibility = (options: UseContainerVisibilityOptions = {}) => {
    const { onNotVisible, threshold = VISIBILITY_THRESHOLD, enabled = true } = options;
    const containerRef = useRef<HTMLDivElement>(null);
    const { getSessionStorage, setSessionStorage } = useSessionStorage();
    const isShowed = useRef(false);

    useEffect(() => {
        const hasChecked = getSessionStorage<boolean>(VISIBILITY_CHECK_KEY, false);

        if (!containerRef.current || hasChecked || !enabled) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;

                setSessionStorage(VISIBILITY_CHECK_KEY, true);

                if (!entry.isIntersecting && !isShowed.current) {
                    isShowed.current = true;
                    onNotVisible?.();
                }
            },
            {
                threshold,
                rootMargin: "0px",
            },
        );

        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [enabled]);

    return containerRef;
};
